/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { styled } from '@mui/system'
import HitCard from 'components/HitCard/HitCard'
import React, { useEffect, useRef, useState } from 'react'
import { LayoutView } from './CampaignLayoutToggler'
import HitsTable from './HitsTable'
import {
  GetHitsQueryVariables,
  Platform,
  SortOrder,
  useGetHitsQuery,
  useGetPlatformsQuery,
  useGetScrapingStatusQuery,
  useMediaAccountsQuery,
} from 'graphql/generated/graphql'
import { Fade, Box } from '@mui/material'
import Spinner from 'components/Spinner/Spinner'
import Toastify from 'components/Toastify/Toastify'
import { useSearchParams } from 'react-router-dom'
import EmptyState from 'components/EmptyState/EmptyState'
import { CardGrid, CardGridWrapper } from './Style'
import PaginationComponent from 'components/Pagination/PaginationComponent'
import { LOCAL, DEVELOP } from 'constants/env'

interface HitsViewProps {
  layout: LayoutView
  campaignId: number
  isHideContent: boolean
  likes: string[]
  profileFollowers: string[]
  categories: string[]
  volumes: string[]
  filterPlatforms: string[]
  setFilterPlatforms: React.Dispatch<React.SetStateAction<any>>
  setCategories: React.Dispatch<React.SetStateAction<any>>
  setIsHideContent: React.Dispatch<React.SetStateAction<boolean>>
  setLikes: React.Dispatch<React.SetStateAction<any>>
}

export interface datePicker {
  start: Date | null
  end: Date | null
}

const config = process.env.NODE_ENV === 'development' ? LOCAL : DEVELOP

interface HitItem {
  id: string
  uq_index?: number
  content?: string | null
  name: string
  date: any // Keep as any or specify a more precise type
  thumbnail?: string
  interaction: {
    positive: number
    shared?: number | null
    followers?: number | null
    tag?: string | null
    following?: number | null
    postCount?: number | null
    bio?: string | null
    verified?: boolean
    comment?: number | null
  }
  platforms: string[]
  creator: {
    name: string // Added name property
    avatarUrl?: string | null
  }
  keywords?: string[]
  campaignId: string // Added campaignId
  postURL: string
  interactedWith: boolean
  markedUnhelpful: boolean // Added markedUnhelpful
  __typename?: 'Hit' | undefined // Added __typename
  [key: string]: any // Allows for dynamic properties
}

type EnhancedHitItem = HitItem & {
  yellowish: boolean
  __typename?: 'Hit'
  id: string
  name: string
  markedUnhelpful: boolean
  campaignId: string
  content?: string | null
  platforms: Platform[]
  keywords: string[]
  campaignName?: string | null
  date?: string | null
  thumbnail?: string | null
  summary?: string | null
  generateCount?: number | null
  uq_index: number
  postURL: string
  interactedWith: boolean
  creator: {
    __typename?: 'Creator'
    name: string
    avatarUrl?: string | null
  }
  interaction: {
    __typename?: 'HitInteraction'
    positive: number
    shared?: number | null
    followers?: number | null
    tag?: string | null
    following?: number | null
    postCount?: number | null
    comment?: number | null
    bio?: string | null
    verified?: boolean
  }
  videoUrl: 'string'
}

export default function HitsView({
  layout,
  campaignId,
  isHideContent,
  likes,
  profileFollowers,
  categories,
  volumes,
  filterPlatforms,
  setFilterPlatforms,
  setCategories,
  setIsHideContent,
  setLikes,
}: HitsViewProps): JSX.Element {
  const [filteredData, setFilteredData] = useState<EnhancedHitItem[]>([])
  // console.log('✌️filteredData --->', filteredData)
  const [userInfoInstagram, setUserInfoInstagram] = useState<any>({})
  // console.log('✌️userInfoInstagram --->', userInfoInstagram)
  const [userInfoTikTok, setUserInfoTikTok] = useState<any>({})
  // console.log('✌️userInfoTikTok --->', userInfoTikTok)
  const [hitGenerationCounts, setHitGenerationCounts] = useState<
    Record<string, number>
  >({})
  const [generatedComments, setGeneratedComments] = useState<
    Record<string, string>
  >({})

  const [checked] = React.useState(true)
  const [searchParams] = useSearchParams()
  const { data: PlatformsData } = useGetPlatformsQuery({
    variables: {
      id: campaignId,
    },
  })
  // TODO Decide do we really need this checks or should I just put param directly into state
  const getSortFromURL = (): string => {
    const sortParam = searchParams.get('sort')
    if (sortParam) {
      if (sortParam === 'date') {
        return 'date'
      } else if (sortParam === 'interactions') {
        return 'interactions'
      } else {
        return 'default'
      }
    } else {
      return 'default'
    }
  }
  const getNetworkFilterFromURL = (): string => {
    const networkParam = searchParams.get('network')
    if (networkParam) {
      if (networkParam === 'youtube') {
        return 'youtube'
      } else if (networkParam === 'reddit') {
        return 'reddit'
      } else if (networkParam === 'twitter') {
        return 'twitter'
      } else {
        return 'all'
      }
    } else {
      return 'all'
    }
  }
  const [networkFilter, setNetworkFitler] = React.useState(
    getNetworkFilterFromURL()
  )
  const [classificationTag, setClassificationTag] = React.useState<string[]>([])
  const [sortBy, setSortBy] = React.useState(getSortFromURL())

  // const [allHits, setAllHits] = useState<Hit[]>([])

  const [cursorAfter, setCursorAfter] = useState<string | null>(null)
  const [cursorBefore, setCursorBefore] = useState<string | null>(null)

  const baseOptions = (): {
    variables: GetHitsQueryVariables
    notifyOnNetworkStatusChange: boolean
    fetchPolicy: 'network-only'
  } => {
    const pagination: GetHitsQueryVariables['pagination'] = {
      limit: 10,
      ...(cursorAfter && { after: cursorAfter }),
      ...(cursorBefore && { before: cursorBefore }),
    }

    const sort =
      sortBy !== 'default'
        ? sortBy === 'date'
          ? { date: SortOrder.Desc }
          : { interactions: SortOrder.Desc }
        : undefined

    const variables: GetHitsQueryVariables = {
      filter: {
        campaignId,
        ...(networkFilter !== 'all' && {
          socialNetwork: networkFilter,
        }),
        ...(categories?.length ? { categories } : {}),
        ...(likes?.length ? { likes } : {}),
        interacted: isHideContent,
      },
      pagination,
      ...(sort && { sort }),
    }

    return {
      variables,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    }
  }
  const { data: statusData, refetch: statusRefetch } =
    useGetScrapingStatusQuery({
      variables: {
        filter: {
          campaignId,
        },
      },
    })
  const { loading, error, data, refetch, networkStatus } = useGetHitsQuery(
    baseOptions()
  )
  const handleNextPage = () => {
    const lastItem = data?.hits?.hits?.at(-1)
    if (lastItem) {
      const index = lastItem?.uq_index ?? 0
      setCursorAfter(index.toString())
      setCursorBefore(null)
    }
  }

  const handlePreviousPage = () => {
    const firstItem = data?.hits?.hits?.[0]
    if (firstItem) {
      const index = firstItem?.uq_index ?? 0

      setCursorBefore(index.toString())
      setCursorAfter(null)
    }
  }

  // Clear all cached hitCounts when new API data is loaded
  // if (networkStatus === 1) {
  //   // Remove all keys that start with "hitCounts-"
  //   Object.keys(localStorage).forEach((key) => {
  //     if (key.startsWith('hitCounts-')) {
  //       localStorage.removeItem(key)
  //     }
  //   })
  // }

  const statusTimeout = useRef<NodeJS.Timer | null>(null)
  const firstRender = useRef<boolean>(true)

  const [currentStatus, setCurrentStatus] = useState({
    instagram: false,
    youtube: false,
    reddit: false,
    tiktok: false,
    facebook: false,
  })

  const checkScrapingStatus = (): void => {
    statusTimeout.current = setInterval((): void => {
      statusRefetch().catch((e) => console.error(e))
    }, 5000)
  }

  const shouldClearInterval = (): boolean => {
    const platforms = PlatformsData?.platforms ?? []
    const finishedState = {
      instagram: false,
      youtube: false,
      reddit: false,
      tiktok: false,
      facebook: false,
    }
    // Instagram
    if (
      platforms.includes(Platform.Instagram) &&
      statusData?.scraping.instagram === false
    ) {
      finishedState.instagram = true
    } else if (!platforms.includes(Platform.Instagram)) {
      finishedState.instagram = true
    } else {
      finishedState.instagram = false
    }
    // Youtube
    if (
      platforms.includes(Platform.Youtube) &&
      statusData?.scraping.youtube === false
    ) {
      finishedState.youtube = true
    } else if (!platforms.includes(Platform.Youtube)) {
      finishedState.youtube = true
    } else {
      finishedState.youtube = false
    }
    // Reddit
    if (
      platforms.includes(Platform.Reddit) &&
      statusData?.scraping.reddit === false
    ) {
      finishedState.reddit = true
    } else if (!platforms.includes(Platform.Reddit)) {
      finishedState.reddit = true
    } else {
      finishedState.reddit = false
    }
    // TikTok
    if (
      platforms.includes(Platform.TikTok) &&
      statusData?.scraping.tiktok === false
    ) {
      finishedState.tiktok = true
    } else if (!platforms.includes(Platform.TikTok)) {
      finishedState.tiktok = true
    } else {
      finishedState.tiktok = false
    }
    // Facebook
    if (
      platforms.includes(Platform.Facebook) &&
      statusData?.scraping.facebook === false
    ) {
      finishedState.facebook = true
    } else if (!platforms.includes(Platform.Facebook)) {
      finishedState.facebook = true
    } else {
      finishedState.facebook = false
    }
    if (
      finishedState.instagram &&
      finishedState.youtube &&
      finishedState.reddit &&
      finishedState.tiktok &&
      finishedState.facebook
    ) {
      return true
    } else {
      return false
    }
  }

  useEffect(() => {
    if (firstRender.current) {
      checkScrapingStatus()
      setCurrentStatus({
        instagram: statusData?.scraping?.instagram ?? false,
        youtube: statusData?.scraping?.youtube ?? false,
        reddit: statusData?.scraping?.reddit ?? false,
        tiktok: statusData?.scraping?.tiktok ?? false,
        facebook: statusData?.scraping.facebook ?? false,
      })
      firstRender.current = false
    } else {
      if (PlatformsData?.platforms.includes(Platform.Instagram)) {
        if (currentStatus.instagram !== statusData?.scraping?.instagram) {
          refetch().catch((e) => console.log(e))
          setCurrentStatus((prev) => {
            return {
              ...prev,
              instagram: statusData?.scraping?.instagram ?? false,
            }
          })
        }
      } else {
        setCurrentStatus((prev) => {
          return {
            ...prev,
            instagram: false,
          }
        })
      }
      if (PlatformsData?.platforms.includes(Platform.Youtube)) {
        if (currentStatus.youtube !== statusData?.scraping?.youtube) {
          refetch().catch((e) => console.log(e))
          setCurrentStatus((prev) => {
            return {
              ...prev,
              youtube: statusData?.scraping?.youtube ?? false,
            }
          })
        }
      } else {
        setCurrentStatus((prev) => {
          return {
            ...prev,
            youtube: false,
          }
        })
      }
      if (PlatformsData?.platforms.includes(Platform.Facebook)) {
        if (currentStatus.facebook !== statusData?.scraping.facebook) {
          refetch().catch((e) => console.log(e))
          setCurrentStatus((prev) => {
            return {
              ...prev,
              facebook: statusData?.scraping.facebook ?? false,
            }
          })
        }
      } else {
        setCurrentStatus((prev) => {
          return {
            ...prev,
            facebook: false,
          }
        })
      }
      if (PlatformsData?.platforms.includes(Platform.Reddit)) {
        if (currentStatus.reddit !== statusData?.scraping?.reddit) {
          refetch().catch((e) => console.log(e))
          setCurrentStatus((prev) => {
            return {
              ...prev,
              reddit: statusData?.scraping?.reddit ?? false,
            }
          })
        }
      } else {
        setCurrentStatus((prev) => {
          return {
            ...prev,
            reddit: false,
          }
        })
      }
      if (PlatformsData?.platforms.includes(Platform.TikTok)) {
        if (currentStatus.tiktok !== statusData?.scraping?.tiktok) {
          refetch().catch((e) => console.log(e))
          setCurrentStatus((prev) => {
            return {
              ...prev,
              tiktok: statusData?.scraping?.tiktok ?? false,
            }
          })
        }
      } else {
        setCurrentStatus((prev) => {
          return {
            ...prev,
            tiktok: false,
          }
        })
      }
    }
    if (shouldClearInterval()) {
      clearInterval(statusTimeout.current ?? 0)
      setCurrentStatus({
        instagram: false,
        reddit: false,
        youtube: false,
        tiktok: false,
        facebook: false,
      })
    }
  }, [statusData])

  const fetchUserInfo = async (username: string) => {
    try {
      const params = new URLSearchParams({
        path: '/v1/info',
        username_or_id_or_url: username,
        include_about: 'true',
        url_embed_safe: 'true',
      })
      const res = await fetch(`${config.API_URL}/api/social-proxy?${params.toString()}`)
      const json = await res.json()
      if (!res.ok || !json.success) return null
      return json.data
    } catch (error) {
      console.error(`Error fetching info for user ${username}:`, error)
      return null
    }
  }

  const {
    data: mediaAccountsInstagram,
    loading: mediaAccountsLoadingInstagram,
  } = useMediaAccountsQuery({
    variables: {
      platform: Platform.Instagram,
    },
    skip: !data?.hits?.hits?.some((hit) =>
      hit.platforms.includes(Platform.Instagram)
    ),
  })
  const { data: mediaAccountsTikTok, loading: mediaAccountsLoadingTikTok } =
    useMediaAccountsQuery({
      variables: {
        platform: Platform.TikTok,
      },
      skip: !data?.hits?.hits?.some((hit) =>
        hit.platforms.includes(Platform.TikTok)
      ),
    })

  const { data: instagramAccounts } = useMediaAccountsQuery({
    variables: { platform: Platform.Instagram },
  })
  const { data: tiktokAccounts } = useMediaAccountsQuery({
    variables: { platform: Platform.TikTok },
  })
  const { data: facebookAccounts } = useMediaAccountsQuery({
    variables: { platform: Platform.Facebook },
  })
  const { data: youtubeAccounts } = useMediaAccountsQuery({
    variables: { platform: Platform.Youtube },
  })
  const { data: redditAccounts } = useMediaAccountsQuery({
    variables: { platform: Platform.Reddit },
  })

  const platformAccounts: { [key: string]: string | undefined } = {
    Instagram: instagramAccounts?.mediaAccounts?.[0]?.accountName,
    TikTok: tiktokAccounts?.mediaAccounts?.[0]?.accountName,
    Facebook: facebookAccounts?.mediaAccounts?.[0]?.accountName,
    Youtube: youtubeAccounts?.mediaAccounts?.[0]?.accountName,
    Reddit: redditAccounts?.mediaAccounts?.[0]?.accountName,
  }
  // console.log('✌️platformAccounts --->', platformAccounts)

  // Find media account
  useEffect(() => {
    if (!data?.hits) return
    const hits = data?.hits?.hits
    const instagramHit = hits.find((hit) =>
      hit.platforms.includes(Platform.Instagram)
    )
    const tiktokHit = hits.find((hit) =>
      hit.platforms.includes(Platform.TikTok)
    )
    // Handle Instagram data
    if (instagramHit && !mediaAccountsLoadingInstagram) {
      const instagramUsername =
        mediaAccountsInstagram?.mediaAccounts[0]?.accountName
      if (instagramUsername) {
        fetchUserInfo(instagramUsername)
          .then((instagramData) => {
            if (instagramData) {
              setUserInfoInstagram(instagramData)
            }
          })
          .catch((error) =>
            console.error('Error fetching Instagram data:', error)
          )
      }
    }
    // Handle TikTok data
    if (tiktokHit && !mediaAccountsLoadingTikTok) {
      const tiktokUsername = mediaAccountsTikTok?.mediaAccounts[0]?.accountName
      if (tiktokUsername) {
        fetchUserInfo(tiktokUsername)
          .then((tiktokData) => {
            if (tiktokData) {
              setUserInfoTikTok(tiktokData)
            }
          })
          .catch((error) => console.error('Error fetching TikTok data:', error))
      }
    }
  }, [
    mediaAccountsInstagram,
    mediaAccountsLoadingInstagram,
    mediaAccountsTikTok,
    mediaAccountsLoadingTikTok,
  ])

  // const extractPostId = (platform: string, url: string): string | null => {
  //   try {
  //     switch (platform) {
  //       case 'Instagram':
  //         const igMatch = url.match(/instagram\.com\/(?:p|reel)\/([^\/\?]+)/)
  //         return igMatch ? igMatch[1] : null
  //       case 'Facebook':
  //         const fbPostMatch = url.match(/\/(?:posts|videos|story|reel)\/(\d+)/)
  //         const fbStoryMatch = url.match(/[?&]story_fbid=(\d+)/)
  //         const fbWatchMatch = url.match(/\/watch\/\?v=(\d+)/)
  //         return fbPostMatch
  //           ? fbPostMatch[1]
  //           : fbStoryMatch
  //           ? fbStoryMatch[1]
  //           : fbWatchMatch
  //           ? fbWatchMatch[1]
  //           : null
  //       case 'Youtube':
  //         const ytMatch = url.match(
  //           /(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([^\/\?&#]+)/
  //         )
  //         return ytMatch ? ytMatch[1] : null
  //       case 'TikTok':
  //         const ttMatch = url.match(/tiktok\.com\/(?:@[\w.-]+\/)?video\/(\d+)/)
  //         return ttMatch ? ttMatch[1] : null
  //       case 'Reddit':
  //         const redditMatch = url.match(
  //           /reddit\.com\/(?:r\/[^\/]+\/)?comments\/([a-zA-Z0-9]+)/
  //         )
  //         return redditMatch ? redditMatch[1] : null
  //       default:
  //         return null
  //     }
  //   } catch (error) {
  //     console.error(`Error extracting post ID for ${platform}:`, error)
  //     return null
  //   }
  // }

  // const fetchUserComment = async (
  //   platform: string,
  //   postUrl: string,
  //   username: string
  // ) => {
  //   try {
  //     const postId = extractPostId(platform, postUrl)
  //     if (!postId) {
  //       console.error(
  //         `Could not extract post ID from URL for ${platform}: ${postUrl}`
  //       )
  //       return null
  //     }
  //     switch (platform) {
  //       case 'Instagram': {
  //         // const accessToken = localStorage.getItem('instagramToken')
  //         const accessToken =
  //           'EAAKawQMf3OABO2njmqSdWtFM5YWKjCj7CZAuYFbGgCqYmPxekNzfFkYZCInr1z8yQT12wnz8ZAJqw9UWE4g1vupKPM9fXAdlmIybG9oKrQZB4hUsQvM59BBfDKD19JYnzK6MT0XA8ijlMG6zRZCAcUs9j1kPdEmWNQ4qZB7Mf0ZBB3sCLJutdmeWrJw4bvK789f63QmOnkw1gsbFecSmQZDZD'
  //         try {
  //           const response = await axios.get(
  //             `https://graph.instagram.com/v17.0/${postId}/comments`,
  //             {
  //               params: {
  //                 access_token: accessToken,
  //                 fields: 'username,text,timestamp',
  //               },
  //               headers: {
  //                 'Content-Type': 'application/json',
  //               },
  //             }
  //           )
  //           if (response.data?.data) {
  //             return (
  //               response?.data?.data?.find(
  //                 (comment: any) => comment.username === username
  //               ) || null
  //             )
  //           }
  //           return null
  //         } catch (error: any) {
  //           console.error('Error fetching Instagram comments:', error)
  //           if (error.response && error.response.status === 400) {
  //             console.error('Bad Request: The content might not be available.')
  //           }
  //           return null
  //         }
  //       }
  //       case 'TikTok': {
  //         // const accessToken = localStorage.getItem('tiktokToken')
  //         const accessToken =
  //           'act.uC5UFar8TrNqQcs3FdYbFBTov3gvhzOJPuW7GT1xvLMVVLujwmjHoByqb7B5!4940.e1'
  //         if (!accessToken) return null
  //         try {
  //           const response = await axios({
  //             method: 'post',
  //             url: 'https://open.tiktokapis.com/v2/research/video/comment/list',
  //             headers: {
  //               Authorization: `Bearer ${accessToken}`,
  //               'Content-Type': 'application/json',
  //             },
  //             data: {
  //               video_id: postId,
  //               max_count: 50,
  //               cursor: 0,
  //             },
  //           })
  //           if (response?.data?.data?.comments) {
  //             return (
  //               response?.data?.data?.comments?.find(
  //                 (comment: any) => comment.user.username === username
  //               ) || null
  //             )
  //           }
  //           return null
  //         } catch (error: any) {
  //           console.error('Error fetching TikTok comments:', error)
  //           if (error.response) {
  //             console.error(
  //               'TikTok API Error:',
  //               error.response.status,
  //               error.response.data
  //             )
  //           } else {
  //             console.error('TikTok API Error:', error.message)
  //           }
  //           return null
  //         }
  //       }
  //       case 'Reddit': {
  //         // const accessToken = localStorage.getItem('redditToken')
  //         const accessToken =
  //           'eyJhbGciOiJSUzI1NiIsImtpZCI6IlNIQTI1NjpzS3dsMnlsV0VtMjVmcXhwTU40cWY4MXE2OWFFdWFyMnpLMUdhVGxjdWNZIiwidHlwIjoiSldUIn0'

  //         // if (!accessToken) return null
  //         const response = await axios.get(
  //           `https://oauth.reddit.com/comments/${postId}`,
  //           {
  //             headers: {
  //               Authorization: `Bearer ${accessToken}`,
  //               'Content-Type': 'application/json',
  //             },
  //           }
  //         )
  //         if (response.data?.[1]?.data?.children) {
  //           const comments = response.data[1].data.children
  //           return (
  //             comments.find((comment: any) => comment.data.author === username)
  //               ?.data || null
  //           )
  //         }
  //         return null
  //       }
  //       case 'Facebook': {
  //         try {
  //           const accessToken =
  //             'EAAKawQMf3OABOZCLZByNmPrHXBgdpE1T7sibbZBOmGZA1HCwajsOEijH8vchrSZAI8JOjRNqUiBQCuPh1qfXOZBQ6vaGXHCkbpOo48lltdM55l5FddXCc7X4eZBpicwCMXIikWw0G7keBTjMlHvzTvJ2sLbeDTTbzXH4pBjUceEZAUBAgo9dUK2GO8b6AQZDZD'
  //           const postResponse = await axios.get(
  //             `https://graph.facebook.com/${postId}/comments`,
  //             {
  //               params: {
  //                 fields: 'from,message',
  //                 access_token: accessToken,
  //               },
  //             }
  //           )
  //           if (!postResponse.data?.id) {
  //             throw new Error('Post not found')
  //           }
  //           // const response = await axios.get(
  //           //   `https://graph.facebook.com/v17.0/${postId}/comments`,
  //           //   {
  //           //     params: {
  //           //       access_token: accessToken,
  //           //       fields:
  //           //         'from{name,id},message,created_time,like_count,comment_count',
  //           //       limit: 100,
  //           //     },
  //           //   }
  //           // )
  //           // if (response.data?.data) {
  //           //   const userComment = response.data.data.find(
  //           //     (comment: any) => comment.from?.name === username
  //           //   )
  //           //   return userComment || null
  //           // }
  //           return null
  //         } catch (error: any) {
  //           console.log('✌️error --->', error)
  //           return null
  //         }
  //       }
  //       case 'Youtube': {
  //         // const accessToken = localStorage.getItem('google_access_token')
  //         const accessToken = 'abfhkfagefuwelgrwler86454365'
  //         // if (!accessToken) return null

  //         const response = await axios.get(
  //           'https://youtube.googleapis.com/youtube/v3/commentThreads',
  //           {
  //             params: {
  //               part: 'snippet',
  //               videoId: postId,
  //               key: accessToken,
  //             },
  //             headers: {
  //               Authorization: `Bearer ${accessToken}`,
  //               'Content-Type': 'application/json',
  //             },
  //           }
  //         )
  //         if (response?.data?.items) {
  //           return (
  //             response?.data?.items?.find(
  //               (item: any) =>
  //                 item.snippet.topLevelComment.snippet.authorDisplayName ===
  //                 username
  //             )?.snippet.topLevelComment || null
  //           )
  //         }
  //         return null
  //       }
  //       default:
  //         return null
  //     }
  //   } catch (error) {
  //     console.log('✌️error --->', error)
  //     // if (axios.isAxiosError(error)) {
  //     //   if (error.response?.status === 429) {
  //     //     console.error('Rate limit exceeded for', platform)
  //     //   } else if (
  //     //     error.response?.status === 401 ||
  //     //     error.response?.status === 403
  //     //   ) {
  //     //     console.error('Authentication failed for', platform)
  //     //   } else {
  //     //     console.error(
  //     //       `Error fetching comment for ${platform}:`,
  //     //       error.message
  //     //     )
  //     //   }
  //     // } else {
  //     //   console.error(`Unknown error for ${platform}:`, error)
  //     // }
  //     // return null
  //   }
  // }

  useEffect(() => {
    let isMounted = true
    const processData = async () => {
      if (!data?.hits?.hits || !isMounted) return
      try {
        let filteredHits = [...data.hits.hits]
        // console.log('✌️filteredHits --->', filteredHits)
        // if (isHideContent) {
        //   filteredHits = filteredHits.filter((item) => !item.interactedWith)
        // }
        // const processedData = await Promise.all(
        //   newDataCopy.map(async (item) => {
        //     if (item.interactedWith) {
        //       const commentPromises = (item.platforms || []).map(
        //         async (platform: string) => {
        //           try {
        //             // const username = platformAccounts[platform]
        //             const username = 'simba.nala.lionpups'

        //             if (username && item?.postURL) {
        //               const userComment = await fetchUserComment(
        //                 platform,
        //                 item.postURL,
        //                 username
        //               )
        //               return {
        //                 platform,
        //                 comment: userComment || null,
        //                 hasUserComment: Boolean(userComment),
        //               }
        //             }
        //           } catch (err) {
        //             console.error(
        //               `Error fetching comment for ${platform}:`,
        //               err
        //             )
        //           }
        //           return {
        //             platform,
        //             comment: null,
        //             hasUserComment: false,
        //           }
        //         }
        //       )
        //       try {
        //         const commentsResults = await Promise.all(commentPromises)
        //         const platformComments = commentsResults.reduce(
        //           (acc, { platform, comment, hasUserComment }) => ({
        //             ...acc,
        //             [`${platform?.toLowerCase()}Comment`]: comment,
        //             [`${platform?.toLowerCase()}HasUserComment`]:
        //               hasUserComment,
        //           }),
        //           {}
        //         )

        //         return {
        //           ...item,
        //           ...platformComments,
        //           hasAnyUserComment: commentsResults.some(
        //             (r) => r?.hasUserComment
        //           ),
        //         }
        //       } catch (err) {
        //         console.error('Error processing comments:', err)
        //         return item
        //       }
        //     }
        //     return item
        //   })
        // )
        // if (!isMounted) return
        // let filteredHits = newDataCopy || []
        // console.log('✌️filteredHits --->', filteredHits)

        // if (isHideContent) {
        //   filteredHits = data?.hits?.filter((item) => !item.interactedWith)
        // }
        // console.log('✌️filteredHits --->', filteredHits)
        // if (likes?.length > 0) {
        //   filteredHits = filteredHits.filter((item) => {
        //     const likeCount = item.interaction.positive
        //     return likes.some((range) => {
        //       const [minStr, maxStr] = range.split('-')
        //       const minStrClean = minStr.replace(/,/g, '').replace('+', '')
        //       const min = Number(minStrClean)
        //       const max = maxStr ? Number(maxStr.replace(/,/g, '')) : undefined
        //       if (max !== undefined) {
        //         return likeCount >= min && likeCount <= max
        //       } else {
        //         return likeCount >= min
        //       }
        //     })
        //   })
        // }
        // console.log('✌️filteredHits --->', filteredHits)

        if (profileFollowers?.length > 0) {
          filteredHits = filteredHits.filter((item) => {
            const followersCount = item.interaction.followers
            return profileFollowers.some((range) => {
              const [minStr, maxStr] = range.split('-')
              const minStrClean = minStr.replace(/,/g, '').replace('+', '')
              const min = Number(minStrClean)
              const max = maxStr ? Number(maxStr.replace(/,/g, '')) : undefined
              if (max !== undefined) {
                return (
                  followersCount !== null &&
                  followersCount !== undefined &&
                  followersCount >= min &&
                  followersCount <= max
                )
              } else {
                return (
                  followersCount !== null &&
                  followersCount !== undefined &&
                  followersCount >= min
                )
              }
            })
          })
        }
        // console.log('✌️filteredHits --->', filteredHits)

        // if (filterPlatforms?.length > 0) {
        //   if (filterPlatforms.includes('all')) {
        //     // "all" selected → return everything (ignore other filters)
        //     // do nothing, keep filteredHits as is
        //   } else {
        //     // filter normally
        //     filteredHits = filteredHits.filter((item) =>
        //       item.platforms.some((value) =>
        //         filterPlatforms.some(
        //           (fp) => fp.toLowerCase() === value.toLowerCase()
        //         )
        //       )
        //     )
        //   }
        //   console.log('after filteredHits --->', filteredHits)
        // }
        // console.log('✌️filteredHits --->', filteredHits)

        // if (categories?.length > 0) {
        //   filteredHits = filteredHits.filter(
        //     (item) =>
        //       item.interaction.tag && categories.includes(item.interaction.tag)
        //   )
        // }
        // console.log('✌️filteredHits --->', filteredHits)

        if (volumes?.length > 0) {
          const creatorCounts: { [creatorName: string]: number } =
            filteredHits.reduce<{ [creatorName: string]: number }>(
              (acc, item) => {
                const creatorName = item.creator.name
                acc[creatorName] = (acc[creatorName] || 0) + 1
                return acc
              },
              {}
            )

          filteredHits = filteredHits.filter((item) => {
            const creatorName = item.creator.name
            const creatorCount = creatorCounts[creatorName] || 0
            return volumes.some((range: string) => {
              const [minStr, maxStr] = range.split('-')
              const minStrClean = minStr.replace(/,/g, '').replace('+', '')
              const min = Number(minStrClean)
              const max = maxStr ? Number(maxStr.replace(/,/g, '')) : undefined

              if (max !== undefined) {
                return creatorCount >= min && creatorCount <= max
              } else {
                return creatorCount >= min
              }
            })
          })
        }
        // console.log('✌️filteredHits --->', filteredHits)
        const newFilteredData = await Promise.all(
          filteredHits?.map(async (item) => {
            const { followers } = item?.interaction
            const mediaData = {
              ...item,
              yellowish: item.platforms.includes(Platform.Instagram)
                ? followers === userInfoInstagram?.follower_count
                : item.platforms.includes(Platform.TikTok)
                ? followers === userInfoTikTok?.follower_count
                : false,
              videoUrl: item.videoUrl ?? item.thumbnail ?? null,
              summary: item.summary ?? null,
              __typename: 'Hit',
            }
            return mediaData
          })
        )
        setFilteredData(newFilteredData as EnhancedHitItem[])
      } catch (error) {
        console.error('Error processing data:', error)
      }
    }
    void processData()
    return () => {
      isMounted = false
    }
  }, [
    data?.hits,
    isHideContent,
    likes,
    profileFollowers,
    categories,
    volumes,
    filterPlatforms,
    userInfoInstagram?.follower_count,
    userInfoTikTok?.follower_count,
  ])

  if (layout === 'grid')
    return (
      <Fade in={checked} timeout={1500}>
        <CardGridWrapper>
          <CardGrid columns={filteredData?.length === 0 ? 1 : 3}>
            {loading ? (
              <Spinner />
            ) : error ? (
              <Toastify />
            ) : filteredData?.length === 0 ? (
              <Box
                sx={{
                  // gridColumnStart: 1,
                  // gridColumnEnd: 6,
                  // gridRowStart: 1,
                  // gridRowEnd: 4,
                  alignSelf: 'center',
                  justifySelf: 'center',
                }}
              >
                <EmptyState statusData={currentStatus} />
              </Box>
            ) : (
              filteredData
                .filter((item: any) => {
                  if (classificationTag.length > 0) {
                    return classificationTag.includes(
                      item.interaction.tag ?? ''
                    )
                  }
                  return true
                })
                .map((hitItem, index) => {
                  return (
                    <HitCard
                      key={hitItem.id}
                      body={hitItem.content ?? ''}
                      headerProps={{
                        ...hitItem,
                        hitItemId: hitItem.id,
                        authorName: hitItem.name,
                        date: Number(hitItem.date),
                        uq_index: Number(hitItem.uq_index) ?? 0,
                        imageSource:
                          hitItem.thumbnail ??
                          'https://media.istockphoto.com/id/1147544806/vector/no-thumbnail-image-vector-graphic.jpg?s=170667a&w=0&k=20&c=-r15fTq303g-Do1h-F1jLdxddwkg4ZTtkdQK1XP2sFk=',
                        reactionCount: hitItem.interaction.positive,
                        shareCount: hitItem.interaction.shared ?? 0,
                        followersCount: hitItem.interaction.followers,
                        platform: hitItem.platforms[0],
                        avatar: hitItem.creator.avatarUrl ?? undefined,
                        keyword: hitItem?.keywords,
                        campaignName: hitItem?.campaignName ?? '',
                        summary: hitItem?.summary ?? undefined,
                        generateCount: hitItem?.generateCount ?? 0,
                        postURL: hitItem.postURL,
                        interactedWith: hitItem.interactedWith,
                        tag: hitItem.interaction.tag,
                        thumbnail: hitItem.thumbnail,
                      }}
                      data={data?.hits?.hits ?? []}
                    />
                  )
                })
            )}
          </CardGrid>
        </CardGridWrapper>
      </Fade>
    )

  return (
    <Wrapper>
      {loading ? (
        <Spinner />
      ) : error ? (
        <Toastify />
      ) : !filteredData ? (
        <Toastify />
      ) : (
        <>
          <HitsTable
            data={{
              __typename: 'Query',
              hits: {
                hits: filteredData,
                hasNextPage: false,
                hasPreviousPage: false,
              },
            }}
            refetch={refetch}
            campaignId={campaignId}
            networkFilter={networkFilter}
            setNetworkFilter={setNetworkFitler}
            sortBy={sortBy}
            setSortBy={setSortBy}
            statusData={currentStatus}
            classificationTag={classificationTag}
            setClassificationTag={setClassificationTag}
            newData={data?.hits?.hits ?? []}
            hitGenerationCounts={hitGenerationCounts}
            setHitGenerationCounts={setHitGenerationCounts}
            categories={categories}
            filterPlatforms={filterPlatforms}
            setFilterPlatforms={setFilterPlatforms}
            setCategories={setCategories}
            setIsHideContent={setIsHideContent}
            setLikes={setLikes}
            likes={likes}
            isHideContent={isHideContent}
            setGeneratedComments={setGeneratedComments}
            generatedComments={generatedComments}
          />
          <PaginationComponent
            hasNext={Boolean(data?.hits?.hasNextPage)}
            hasPrev={Boolean(data?.hits?.hasPreviousPage)}
            onNext={handleNextPage}
            onPrev={handlePreviousPage}
          />
        </>
      )}
    </Wrapper>
  )
}

const Wrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
})
